/**
 * @file PlanTypeList.js
 * @description Admin list of plan types with search, filter, and sorting.
 */

import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Popover, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import styles from './PlanYear.module.scss';
import modalStyles from './PlanTypeDisplayProperties.module.scss';
import { FilterModal, renderFilterIcon } from '../helpers/FilterComponent';
import EmptyTableRow from '../ui/no_data_indicators/EmptyTableRow';
import PaginationComponent from '../helpers/PaginationComponent';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import {
    searchPlanTypes,
    deletePlanType,
    deletePlanTypes,
} from '../../services/benefitPlanService';
import { ROUTES } from '../../constants/routes';
import PlanTypeDisplayPropertiesModal from './PlanTypeDisplayPropertiesModal';
import PlanYearDropdown from '../shared/asyncDropdowns/PlanYearDropdown';
import {
    getDisplayModePill,
    getEffectivePlanTypeSortOrder,
    resolveDisplayMode,
} from './_helpers/planTypeDisplayMode';
import { getText } from '../../i18n';

const ITEMS_PER_PAGE = 20;

const SortOptions = {
    DEFAULT: 'default', // ClientPlanType.sortOrder ?? planType.sortOrder
    NAME: 'name',
    DISPLAY: 'display',
};
const OrderOptions = { ASCENDING: 'asc', DESCENDING: 'desc' };

const isSystemRow = pt => pt?.planType?.tenantId == null;

/**
 * Zero is a legitimate `sortOrder` for a single row, so only treat it as a
 * "not set" placeholder when it shows up on more than one root item.
 */
const hasStaleZeroSortOrders = list =>
    list.filter(pt => Number(pt?.sortOrder) === 0).length > 1;

/**
 * Normalizes a ClientPlanType list so top-level `sortOrder` values that look
 * like stale 0 placeholders fall back to the nested `planType.sortOrder`.
 * Leaves the list untouched when at most one row has a 0, since that's a
 * valid position rather than a sign the field wasn't populated.
 */
const normalizePlanTypeSortOrders = list => {
    if (!hasStaleZeroSortOrders(list)) return list;
    return list.map(pt =>
        Number(pt?.sortOrder) === 0
            ? { ...pt, sortOrder: Number(pt?.planType?.sortOrder) || 0 }
            : pt,
    );
};

const PlanTypeList = () => {
    const { showModal } = useContext(ModalContext);
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const [planTypeList, setPlanTypeList] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const [sortConfig, setSortConfig] = useState({
        sort: SortOptions.DEFAULT,
        order: OrderOptions.ASCENDING,
    });
    const [activeFilterModal, setActiveFilterModal] = useState(null);
    const [selections, setSelections] = useState({});

    const [selectedIds, setSelectedIds] = useState([]);

    const [selectedPlanYearId, setSelectedPlanYearId] = useState(null);
    const [selectedPlanYearLabel, setSelectedPlanYearLabel] = useState('');

    const [modalState, setModalState] = useState({
        open: false,
        viewModel: null,
        isAddMode: false,
    });

    const [popGroupAnchorEl, setPopGroupAnchorEl] = useState(null);
    const [popGroupOverflow, setPopGroupOverflow] = useState([]);
    const popGroupPopoverOpen = Boolean(popGroupAnchorEl);
    const popGroupPopoverId = popGroupPopoverOpen
        ? 'plan-type-pop-groups-popover'
        : undefined;

    const handlePopGroupMoreClick = (event, overflowGroups) => {
        event.stopPropagation();
        setPopGroupAnchorEl(event.currentTarget);
        setPopGroupOverflow(overflowGroups);
    };

    const handlePopGroupPopoverClose = () => {
        setPopGroupAnchorEl(null);
        setPopGroupOverflow([]);
    };

    const handlePlanYearChange = (planYearId, planYearObj) => {
        setSelectedPlanYearId(planYearId);
        setSelectedPlanYearLabel(planYearObj?.name || '');
    };

    function handlePlanYearsLoaded(years) {
        if (years.length === 0) setIsLoading(false);
    }

    const loadPlanTypes = useCallback(async planYearId => {
        if (!planYearId) return;
        try {
            setIsLoading(true);
            const list = await searchPlanTypes(null, planYearId);
            const normalizedList = normalizePlanTypeSortOrders(list);
            setPlanTypeList(normalizedList);
        } catch (error) {
            console.error('Failed to fetch plan type list', error);
            Toast.error(
                error?.response?.data?.detail ||
                    'There was an issue loading the plan type list.',
            );
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPlanTypes(selectedPlanYearId);
    }, [selectedPlanYearId, loadPlanTypes]);

    // ──────────────── filter / sort ────────────────

    const applyFiltersAndSort = useCallback(() => {
        let rows = [...planTypeList];

        if (searchTerm) {
            const q = searchTerm.toLowerCase();
            rows = rows.filter(pt => {
                const name = (pt.name || pt.planType?.name || '').toLowerCase();
                const code = (pt.code || pt.planType?.code || '').toLowerCase();
                return name.includes(q) || code.includes(q);
            });
        }

        Object.entries(selections).forEach(([column, values]) => {
            if (!values?.length) return;
            rows = rows.filter(pt => {
                if (column === 'name') {
                    return values.includes(pt.name || pt.planType?.name);
                }
                if (column === 'display') {
                    return values.includes(
                        getDisplayModePill(resolveDisplayMode(pt)).label,
                    );
                }
                return true;
            });
        });

        rows.sort((a, b) => {
            const aName = a.name || a.planType?.name || '';
            const bName = b.name || b.planType?.name || '';
            const dir = sortConfig.order === OrderOptions.ASCENDING ? 1 : -1;

            if (sortConfig.sort === SortOptions.NAME) {
                return aName.localeCompare(bName) * dir;
            }
            if (sortConfig.sort === SortOptions.DISPLAY) {
                const aLbl = getDisplayModePill(resolveDisplayMode(a)).label;
                const bLbl = getDisplayModePill(resolveDisplayMode(b)).label;
                return aLbl.localeCompare(bLbl) * dir;
            }
            // DEFAULT: ClientPlanType.sortOrder (override) → planType.sortOrder
            const cmp =
                getEffectivePlanTypeSortOrder(a) -
                getEffectivePlanTypeSortOrder(b);
            if (cmp !== 0) return cmp * dir;
            // Stable tiebreaker so rows don't shuffle when sortOrder ties.
            return aName.localeCompare(bName) * dir;
        });

        setFilteredData(rows);
    }, [planTypeList, searchTerm, selections, sortConfig]);

    useEffect(() => {
        applyFiltersAndSort();
    }, [applyFiltersAndSort]);

    // ──────────────── paging / search ────────────────

    const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
    const pageRows = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredData.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredData, currentPage]);

    const handleSearch = e => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleSort = sort => {
        setSortConfig(prev => ({
            sort,
            order:
                prev.sort === sort && prev.order === OrderOptions.ASCENDING
                    ? OrderOptions.DESCENDING
                    : OrderOptions.ASCENDING,
        }));
    };

    const clearFilters = () => {
        setSelections({});
        setSearchTerm('');
        setCurrentPage(1);
        setSortConfig({
            sort: SortOptions.DEFAULT,
            order: OrderOptions.ASCENDING,
        });
    };

    const getFilterOptions = column => {
        return Array.from(
            new Set(
                planTypeList.map(pt => {
                    if (column === 'name') return pt.name || pt.planType?.name;
                    if (column === 'display')
                        return getDisplayModePill(resolveDisplayMode(pt)).label;
                    return null;
                }),
            ),
        )
            .filter(Boolean)
            .sort((a, b) => String(a).localeCompare(String(b)));
    };

    // ──────────────── selection ────────────────

    const toggleSelect = id =>
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
        );

    const toggleSelectAll = () => {
        const allIds = pageRows.map(r => r.id);
        const allSelected = allIds.every(id => selectedIds.includes(id));
        setSelectedIds(prev =>
            allSelected
                ? prev.filter(id => !allIds.includes(id))
                : Array.from(new Set([...prev, ...allIds])),
        );
    };

    const selectedRows = useMemo(
        () => planTypeList.filter(pt => selectedIds.includes(pt.id)),
        [planTypeList, selectedIds],
    );

    // ──────────────── actions ────────────────

    const openEdit = pt =>
        setModalState({ open: true, viewModel: pt, isAddMode: false });
    const openAdd = () =>
        setModalState({ open: true, viewModel: null, isAddMode: true });
    const closeModal = () =>
        setModalState({ open: false, viewModel: null, isAddMode: false });

    const handleModalSaved = () => {
        closeModal();
        setSelectedIds([]);
        loadPlanTypes(selectedPlanYearId);
    };

    const handleDuplicate = () => {
        if (selectedRows.length !== 1) {
            Toast.error(
                selectedRows.length === 0
                    ? 'Select one plan type to duplicate.'
                    : 'Select only one plan type to duplicate.',
            );
            return;
        }
        const target = selectedRows[0];
        setModalState({ open: true, viewModel: target, isAddMode: true });
    };

    const handleDelete = () => {
        if (selectedRows.length === 0) {
            Toast.error('Select at least one plan type to delete.');
            return;
        }
        if (selectedRows.some(isSystemRow)) {
            Toast.error('System plan types cannot be deleted.');
            return;
        }
        const isBulk = selectedRows.length > 1;
        showModal({
            type: ModalTypes.ACTION,
            title: isBulk ? 'Delete plan types?' : 'Delete plan type?',
            text: isBulk
                ? `This will permanently delete ${selectedRows.length} plan types.`
                : `This will permanently delete "${selectedRows[0].name || selectedRows[0].planType?.name}".`,
            yesButtonText: getText('msg_f6fdbe48dc54'),
            noButtonText: getText('msg_77dfd2135f4d'),
            onYes: async () => {
                try {
                    if (isBulk) {
                        await deletePlanTypes(selectedRows.map(r => r.id));
                    } else {
                        await deletePlanType(selectedRows[0].id);
                    }
                    Toast.success(
                        isBulk
                            ? `${selectedRows.length} plan types deleted.`
                            : 'Plan type deleted.',
                    );
                    setSelectedIds([]);
                    loadPlanTypes(selectedPlanYearId);
                } catch (error) {
                    console.error('Delete failed', error);
                    Toast.error(
                        error?.response?.data?.detail ||
                            'Failed to delete plan type.',
                    );
                }
            },
            onNo: () => {},
        });
    };

    // ──────────────── render ────────────────

    const renderSortArrow = sort => {
        if (sortConfig.sort !== sort) return null;
        return (
            <span className="mx-2">
                <svg width="11" height="6" viewBox="0 0 11 6" fill="none">
                    <path
                        d={
                            sortConfig.order === OrderOptions.ASCENDING
                                ? 'M1 5.25L5.25 0.75L9.5 5.25'
                                : 'M1 0.75L5.25 5.25L9.5 0.75'
                        }
                        stroke="#2F2B43"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        );
    };

    const renderDisplayPill = pt => {
        const { className, label } = getDisplayModePill(resolveDisplayMode(pt));
        return <span className={className}>{label}</span>;
    };

    const MAX_VISIBLE_POP_GROUPS = 3;

    const renderPopulationGroups = pt => {
        const groups = pt.populationGroups || [];
        if (!groups.length) return <span>-</span>;

        const visible = groups.slice(0, MAX_VISIBLE_POP_GROUPS);
        const overflow = groups.slice(MAX_VISIBLE_POP_GROUPS);
        const hiddenCount = overflow.length;

        return (
            <div
                className={`d-flex flex-wrap align-items-center ${styles.popGroupCellWrap}`}
            >
                {visible.map(g => (
                    <span key={g.id} className={styles.groupBadge}>
                        {g.name}
                    </span>
                ))}
                {hiddenCount > 0 && (
                    <span
                        className={styles.moreBadge}
                        role="button"
                        tabIndex={0}
                        onClick={event =>
                            handlePopGroupMoreClick(event, overflow)
                        }
                        onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handlePopGroupMoreClick(event, overflow);
                            }
                        }}
                    >
                        + {hiddenCount} {getText('msg_e7c95b4c2824')}
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center py-3">
                        <div className="float-start d-flex align-items-center d-flex flex-direction-column align-items-baseline">
                            <p className="page-title card-title ms-4 me-5 mb-0">
                                {getText('msg_35a092cd8026')}
                            </p>
                            <p
                                className={`ing-portals-subtitle mt-1 ${styles.planTypesSubtitle}`}
                            >
                                {getText('msg_c2271985817c')}
                            </p>
                        </div>
                        <div className="float-end d-flex align-items-center align-self-baseline">
                            <span
                                type="button"
                                onClick={handleDelete}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-trash me-2" />
                                {getText('msg_f6fdbe48dc54')}
                            </span>
                            <span
                                type="button"
                                onClick={handleDuplicate}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-files me-2" />
                                {getText('msg_972d57379db3')}
                            </span>
                            <span
                                type="button"
                                onClick={clearFilters}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                {getText('msg_381c4bf1d409')}
                            </span>
                            <span
                                type="button"
                                onClick={openAdd}
                                className="ms-3 me-4 button-base ing-button-primary-left align-middle"
                            >
                                <i className="bi bi-plus-lg me-1" />
                                {getText('msg_61cc55aa0453')}
                            </span>
                        </div>
                    </div>

                    {/* Search + Plan Year + Arrange */}
                    <section
                        className={`card-title mx-4 mb-4 d-flex flex-wrap overflow-visible ${styles.filtersRow}`}
                    >
                        <div className="form-group col-12 col-sm-12 col-md-12 col-lg-6 position-relative mb-3">
                            <input
                                id="planTypeSearch"
                                type="text"
                                className="form-control pe-5"
                                placeholder={getText('msg_bce06414177f')}
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                            <i
                                className="bi bi-search position-absolute"
                                style={{
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#667085',
                                    pointerEvents: 'none',
                                }}
                            />
                        </div>
                        <div
                            className={`form-group col-12 col-sm-6 ${styles.planYearFilterCol}`}
                        >
                            <PlanYearDropdown
                                inlineLabel={false}
                                selectedPlanYear={selectedPlanYearId}
                                onPlanYearChange={handlePlanYearChange}
                                onPlanYearsLoaded={handlePlanYearsLoaded}
                                returnFullObject
                                autoSelectCurrent
                            />
                        </div>
                        <span
                            type="button"
                            className={`ing-text-buttons mb-4 ms-auto ${styles.arrangeLink}`}
                            onClick={() => navigate(ROUTES.PLAN_TYPES_ARRANGE)}
                        >
                            {getText('msg_9c716d563fde')}
                        </span>
                    </section>

                    {isLoading ? (
                        <div className="text-center mt-5">
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        </div>
                    ) : (
                        <div className="table-responsive-xxl w-100">
                            <table
                                className={`table table-hover align-middle ${styles.planTypeTable}`}
                            >
                                <colgroup>
                                    <col className={styles.colSelect} />
                                    <col className={styles.colName} />
                                    <col className={styles.colCode} />
                                    <col className={styles.colDisplay} />
                                    <col className={styles.colGroups} />
                                </colgroup>
                                <thead className="custom-table-header table-light">
                                    <tr>
                                        <th className={`ps-4 ${styles.noWrap}`}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={
                                                    pageRows.length > 0 &&
                                                    pageRows.every(r =>
                                                        selectedIds.includes(
                                                            r.id,
                                                        ),
                                                    )
                                                }
                                                onChange={toggleSelectAll}
                                            />
                                        </th>
                                        <th
                                            className={`position-relative ${styles.sortableHeader}`}
                                            onClick={() =>
                                                handleSort(SortOptions.NAME)
                                            }
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_709a23220f2c')}{' '}
                                                {renderSortArrow(
                                                    SortOptions.NAME,
                                                )}
                                            </span>
                                            <span
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setActiveFilterModal(
                                                        'name',
                                                    );
                                                }}
                                                className={`${styles.cursorPointer} pe-3`}
                                            >
                                                {renderFilterIcon(
                                                    selections['name']?.length >
                                                        0,
                                                )}
                                            </span>
                                            {activeFilterModal === 'name' && (
                                                <FilterModal
                                                    column="name"
                                                    options={getFilterOptions(
                                                        'name',
                                                    )}
                                                    selectedOptions={
                                                        selections['name'] || []
                                                    }
                                                    onApply={(col, opts) => {
                                                        setSelections(prev => ({
                                                            ...prev,
                                                            [col]: opts,
                                                        }));
                                                        setCurrentPage(1);
                                                    }}
                                                    onClose={() =>
                                                        setActiveFilterModal(
                                                            null,
                                                        )
                                                    }
                                                />
                                            )}
                                        </th>
                                        <th>
                                            <span className="table-column-header-text">
                                                {getText('msg_adac69379a62')}
                                            </span>
                                        </th>
                                        <th
                                            className={`position-relative ${styles.sortableHeader}`}
                                            onClick={() =>
                                                handleSort(SortOptions.DISPLAY)
                                            }
                                        >
                                            <span className="table-column-header-text">
                                                {getText('msg_574ff9b0c49c')}{' '}
                                                {renderSortArrow(
                                                    SortOptions.DISPLAY,
                                                )}
                                            </span>
                                            <span
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setActiveFilterModal(
                                                        'display',
                                                    );
                                                }}
                                                className={`${styles.cursorPointer} pe-3`}
                                            >
                                                {renderFilterIcon(
                                                    selections['display']
                                                        ?.length > 0,
                                                )}
                                            </span>
                                            {activeFilterModal ===
                                                'display' && (
                                                <FilterModal
                                                    column="display"
                                                    options={getFilterOptions(
                                                        'display',
                                                    )}
                                                    selectedOptions={
                                                        selections['display'] ||
                                                        []
                                                    }
                                                    onApply={(col, opts) => {
                                                        setSelections(prev => ({
                                                            ...prev,
                                                            [col]: opts,
                                                        }));
                                                        setCurrentPage(1);
                                                    }}
                                                    onClose={() =>
                                                        setActiveFilterModal(
                                                            null,
                                                        )
                                                    }
                                                />
                                            )}
                                        </th>
                                        <th>
                                            <span className="table-column-header-text">
                                                {getText('msg_e744dd6b3c48')}
                                            </span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageRows.length === 0 && (
                                        <EmptyTableRow
                                            colSpan={5}
                                            title={getText('msg_619956050f78')}
                                            description={getText(
                                                planTypeList.length === 0
                                                    ? 'msg_d55d91951f3f'
                                                    : 'msg_34a8a584bd72',
                                            )}
                                        />
                                    )}
                                    {pageRows.map(pt => (
                                        <tr key={pt.id}>
                                            <td className="ps-4">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selectedIds.includes(
                                                        pt.id,
                                                    )}
                                                    onChange={() =>
                                                        toggleSelect(pt.id)
                                                    }
                                                    onClick={e =>
                                                        e.stopPropagation()
                                                    }
                                                />
                                            </td>
                                            <td
                                                className={styles.titleCell}
                                                onClick={() => openEdit(pt)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={e => {
                                                    if (
                                                        e.key === 'Enter' ||
                                                        e.key === ' '
                                                    )
                                                        openEdit(pt);
                                                }}
                                            >
                                                {pt.name || pt.planType?.name}
                                            </td>
                                            <td className={styles.subtitleCell}>
                                                {pt.code ||
                                                    pt.planType?.code ||
                                                    '-'}
                                            </td>
                                            <td>{renderDisplayPill(pt)}</td>
                                            <td
                                                className={
                                                    styles.overflowHidden
                                                }
                                            >
                                                {renderPopulationGroups(pt)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {planTypeList.length > 0 && totalPages > 1 && (
                        <PaginationComponent
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    )}
                </div>
            </div>

            {modalState.open && (
                <PlanTypeDisplayPropertiesModal
                    viewModel={modalState.viewModel}
                    isAddMode={modalState.isAddMode}
                    planYearId={selectedPlanYearId}
                    planYearLabel={selectedPlanYearLabel}
                    onHide={closeModal}
                    onSaved={handleModalSaved}
                />
            )}

            <Popover
                id={popGroupPopoverId}
                open={popGroupPopoverOpen}
                anchorEl={popGroupAnchorEl}
                onClose={handlePopGroupPopoverClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        maxWidth: '600px',
                        width: '100%',
                        padding: '16px',
                    }}
                >
                    {popGroupOverflow.map(g => (
                        <span key={g.id} className={styles.groupBadge}>
                            {g.name}
                        </span>
                    ))}
                </Box>
            </Popover>
        </div>
    );
};

export default PlanTypeList;
